import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import './PatientProspectSignup.css'
import PathwayPicker from './PathwayPicker'
import ConfidentialityTeaser from './ConfidentialityTeaser'
import MobileOtpStep from './MobileOtpStep'
import { PROSPECT_DRAFT_KEY, PROSPECT_DRAFT_TTL_MS, PATHWAYS } from './constants'
import {
  isLoggedIn,
  signupPatientProspect,
} from 'src/consumer/services/patientProspectService'

// Top-level orchestrator for the public Patient-Prospect signup flow at
// /care/get-started.
//
// **Anonymous user:** 3 steps — PathwayPicker → ConfidentialityTeaser →
// MobileOtpStep. On success the mock service stores a session and we
// navigate to /care/status.
//
// **Already-logged-in user** (any prior wellness or other signup):
// the OTP/account-creation steps don't make sense — we don't need a new
// account. Instead, on pathway pick we save the draft and immediately
// route to /care/status, where the user's existing session is used to
// drive the post-login intake flow. If a draft already exists from a
// previous anonymous attempt, we skip even the pathway pick and go
// straight to /care/status. This handles three scenarios cleanly:
//   1. User picked pathway anonymously, signed up via wellness modal,
//      came back → resumes at /care/status.
//   2. User was already logged in, clicked "Get matched with a
//      specialist" CTA → picks pathway → goes to /care/status.
//   3. User picked pathway, refreshed → resumes via draft.

function readDraft() {
  try {
    const raw = window.localStorage.getItem(PROSPECT_DRAFT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > PROSPECT_DRAFT_TTL_MS) {
      window.localStorage.removeItem(PROSPECT_DRAFT_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeDraft(draft) {
  try {
    window.localStorage.setItem(
      PROSPECT_DRAFT_KEY,
      JSON.stringify({ ...draft, savedAt: Date.now() }),
    )
  } catch {
    // ignore
  }
}

function clearDraft() {
  try {
    window.localStorage.removeItem(PROSPECT_DRAFT_KEY)
  } catch {
    // ignore
  }
}

export default function PatientProspectSignup() {
  const [searchParams] = useSearchParams()

  const draft = useMemo(readDraft, [])
  const alreadySignedIn = useMemo(isLoggedIn, [])

  // Guard: if the persisted draft says we are past the pathway pick but the
  // resolved pathway never made it to disk (older draft shape, partial write,
  // or a resolved object that lost `pathway`), force the user back to step 1.
  // Without this we'd land on MobileOtpStep with resolvedPathway=null and the
  // /initialize call would POST `{ pathway: undefined }` — which the backend
  // rejects with 4402 "pathway is required." (HTTP 400). Better to re-pick
  // than to fail at submit.
  const draftIsUsable =
    !!draft &&
    !!draft.resolved &&
    typeof draft.resolved.pathway === 'string' &&
    draft.resolved.pathway.length > 0

  const [step, setStep] = useState(draftIsUsable ? draft.step || 1 : 1)
  const [topId, setTopId] = useState(draftIsUsable ? draft.topId || null : null)
  const [subId, setSubId] = useState(draftIsUsable ? draft.subId || null : null)
  const [resolved, setResolved] = useState(draftIsUsable ? draft.resolved : null)

  // Already-signed-in user with a complete pathway selection persisted
  // in localStorage from a previous visit → finish what they started.
  // Re-runs /initialize using the saved pick, which catches the case
  // where their first attempt's OTP succeeded but /initialize 4xx'd
  // (they have a draft + a token, but no prospect role and no case).
  // Only fires on mount — fresh in-session picks are handled by
  // handlePathwayPick directly so we don't double-fire when setResolved
  // updates state in the same tick.
  useEffect(() => {
    let cancelled = false
    if (!(alreadySignedIn && draftIsUsable && draft?.resolved)) return undefined
    ;(async () => {
      try {
        await signupPatientProspect({
          name: null,
          mobile: null,
          pathway: draft.resolved.pathway,
          specialtyTag: draft.resolved.specialtyTag,
          subjectType: 'SELF',
          source: 'WEB',
        })
        if (cancelled) return
        clearDraft()
        window.location.assign('/care/status')
      } catch (e) {
        if (cancelled) return
        const reason = e?.message || 'init_failed'
        window.location.assign(
          `/care/status?initFailed=1&reason=${encodeURIComponent(reason)}`,
        )
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Deep-link support: ?initial=mental-psychiatry etc skips the pathway
  // pick. Useful for marketing CTAs that already know the audience.
  useEffect(() => {
    const init = searchParams.get('initial')
    if (!init || step !== 1 || resolved) return
    const top = PATHWAYS.find((p) => init.startsWith(p.id))
    if (!top) return
    if (top.subOptions) {
      const sub = top.subOptions.find((s) => init === s.id)
      if (!sub) return
      setTopId(top.id)
      setSubId(sub.id)
      setResolved({
        pathway: sub.pathway,
        specialtyTag: sub.specialtyTag,
        topLabel: top.label,
        subLabel: sub.label,
      })
    } else {
      setTopId(top.id)
      setResolved({
        pathway: top.pathway,
        specialtyTag: top.specialtyTag,
        topLabel: top.label,
        subLabel: null,
      })
    }
    // For anonymous users we still need confidentiality + OTP, so step
    // becomes 2. For signed-in users the redirect-to-status effect above
    // takes over instead.
    setStep(2)
  }, [searchParams, step, resolved])

  const handlePathwayPick = async (picked) => {
    setTopId(picked.topId)
    setSubId(picked.subId)
    const nextResolved = {
      pathway: picked.pathway,
      specialtyTag: picked.specialtyTag,
      topLabel: picked.topLabel,
      subLabel: picked.subLabel,
    }
    setResolved(nextResolved)

    if (alreadySignedIn) {
      // The user is already authenticated (wellness account, prior
      // prospect attempt, etc.) so the OTP/account-creation steps don't
      // apply — but we still need to make the /me/patient-prospect/
      // initialize call so they actually get the prospect role + a
      // CareIntakeCase. Previously we just saved a draft and redirected
      // to /care/status, which left users with a failed first attempt
      // unable to retry: they'd loop through pathway pick → status →
      // pathway pick because /care/status had no record to find.
      writeDraft({
        step: 99,
        topId: picked.topId,
        subId: picked.subId,
        resolved: nextResolved,
      })
      try {
        await signupPatientProspect({
          name: null,
          mobile: null,
          pathway: nextResolved.pathway,
          specialtyTag: nextResolved.specialtyTag,
          subjectType: 'SELF',
          source: 'WEB',
        })
        clearDraft()
        window.location.assign('/care/status')
      } catch (e) {
        const reason = e?.message || 'init_failed'
        window.location.assign(
          `/care/status?initFailed=1&reason=${encodeURIComponent(reason)}`,
        )
      }
      return
    }

    writeDraft({
      step: 2,
      topId: picked.topId,
      subId: picked.subId,
      resolved: nextResolved,
    })
    setStep(2)
  }

  // Hard nav (not SPA navigate) so the auth-aware shell — header,
  // login button, ConsumerLayout's RootLanding — re-reads the freshly
  // persisted token from localStorage. Without the reload the user
  // sees /care/status loaded inside a layout that still shows the
  // "Login" button, which is confusing because they ARE signed in.
  // Mirrors handleAfterLoginCAMC's `window.location.reload()` pattern
  // used after wellness OTP login.
  const handleSignedUp = () => {
    clearDraft()
    window.location.assign('/care/status')
  }

  const goBackTo = (target) => {
    setStep(target)
    writeDraft({ step: target, topId, subId, resolved })
  }

  if (step === 1) {
    return (
      <PathwayPicker
        initialTopId={topId}
        initialSubId={subId}
        onPick={handlePathwayPick}
      />
    )
  }
  if (step === 2) {
    return (
      <ConfidentialityTeaser
        pathwayLabel={resolved?.subLabel || resolved?.topLabel}
        onBack={() => goBackTo(1)}
        onContinue={() => {
          writeDraft({ step: 3, topId, subId, resolved })
          setStep(3)
        }}
      />
    )
  }
  return (
    <MobileOtpStep
      resolvedPathway={resolved}
      onBack={() => goBackTo(2)}
      onSignedUp={handleSignedUp}
    />
  )
}
